import React, {
  type MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type Track, useTrack } from '../../../webrtc/webRTCRoom'
import { useSelectedAudioOutputDevice } from '../../../Devices/useDevices'
import { getRole } from '../../../global/blocs/roomInfo'

import './VideoTrack.scss'

const mimicCanvas = new WeakMap<
  MediaStreamTrack,
  {
    mainCanvas: OffscreenCanvas
    mimics: Set<HTMLCanvasElement>
    lastRender: DOMHighResTimeStamp
    attachment: number
  }
>()

const getMimics = (stream: MediaStreamTrack) => {
  if (mimicCanvas.has(stream)) return mimicCanvas.get(stream)!
  const newMimic = {
    mainCanvas: new OffscreenCanvas(256, 144),
    mimics: new Set<HTMLCanvasElement>(),
    lastRender: -Infinity,
    attachment: 0,
  }
  mimicCanvas.set(stream, newMimic)
  return newMimic
}

const attachMimic = (
  stream: MediaStreamTrack,
  canvas: HTMLCanvasElement | null
) => {
  if (!canvas) return
  const mimics = getMimics(stream)
  mimics.mimics.add(canvas)
}

const removeMimic = (
  stream: MediaStreamTrack,
  canvas: HTMLCanvasElement | null
) => {
  if (!canvas) return
  const mimics = getMimics(stream)
  mimics.mimics.delete(canvas)
}

const renderMimics = (
  stream: MediaStreamTrack,
  videoRef: MutableRefObject<HTMLVideoElement | null>,
  force?: boolean
) => {
  const mimics = getMimics(stream)
  const canvas = mimics.mainCanvas
  const streamSettings = stream.getSettings()
  if (streamSettings.aspectRatio) {
    canvas.width = streamSettings.aspectRatio * canvas.height
  }
  const ctx = canvas.getContext('2d')
  const attachment = ++mimics.attachment

  const render = (now?: DOMHighResTimeStamp) => {
    if (videoRef.current && ctx) {
      if (!videoRef.current.muted) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      if (now) mimics.lastRender = now
      mimics.mimics.forEach((c) => {
        const mimicCtx = c.getContext('2d')
        if (mimicCtx) {
          mimicCtx.clearRect(0, 0, c.width, c.height)
          if (videoRef.current?.muted) {
            return
          }

          // TODO: calculate zooming and cropping at the time of canvas draw to avoid quality loss post zoom.

          if (c.dataset.fit === 'cover') {
            const dAR = c.clientWidth / c.clientHeight
            const adjustedWidth = Math.min(canvas.width, canvas.height * dAR)
            const adjustedHeight = Math.min(canvas.height, canvas.width / dAR)

            mimicCtx.drawImage(
              canvas,
              (canvas.width - adjustedWidth) / 2,
              (canvas.height - adjustedHeight) / 2,
              adjustedWidth,
              adjustedHeight,
              0,
              0,
              c.width,
              c.height
            )
          } else {
            const clientAR = c.clientWidth / c.clientHeight
            const sourceAR = canvas.width / canvas.height
            const drawWidth =
              sourceAR >= clientAR ? c.width : c.width * (sourceAR / clientAR)
            const drawHeight =
              sourceAR >= clientAR ? c.height * (clientAR / sourceAR) : c.height
            mimicCtx.drawImage(
              canvas,
              0,
              0,
              canvas.width,
              canvas.height,
              (c.width - drawWidth) / 2,
              (c.height - drawHeight) / 2,
              drawWidth,
              drawHeight
            )
          }
        }
      })
    }
  }

  const throttledRender = (now: DOMHighResTimeStamp) => {
    if (now - mimics.lastRender > 0) render(now)
    if (attachment >= mimics.attachment) {
      videoRef.current?.requestVideoFrameCallback(throttledRender)
    }
  }

  if (force) {
    render()
  } else {
    videoRef.current?.requestVideoFrameCallback(throttledRender)
  }
}

interface VideoTrackProps {
  track: Track
  // Improve performance by using canvas to display video instead of having to decode the video multiple times
  isMimic?: boolean
  fit?: 'cover' | 'contain'
}

const VideoTrack = ({ track, fit, isMimic = false }: VideoTrackProps) => {
  if (isMimic) {
    return <MimicVideoTrack track={track} fit={fit} />
  }

  return <MainVideoTrack track={track} />
}

const MainVideoTrack = ({ track }: Pick<VideoTrackProps, 'track'>) => {
  const audioOutputDeviceId = useSelectedAudioOutputDevice()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [reactiveVideoRef, setReactiveVideoRef] =
    useState<HTMLVideoElement | null>(null)
  useTrack(track, reactiveVideoRef)

  const role = getRole()

  const isMuted =
    ((role === 'presenter' || role === 'producer') && !audioOutputDeviceId) ||
    !track.isEnabled

  useEffect(() => {
    if (isMuted) {
      renderMimics(track.mediaStream, videoRef, true)
    }
  }, [isMuted, track.mediaStream])

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      muted={isMuted}
      disablePictureInPicture
      ref={(ref) => {
        videoRef.current = ref
        setReactiveVideoRef(ref)
        renderMimics(track.mediaStream, videoRef)
      }}
      playsInline
      style={{
        display: 'block',
        height: '100%',
        width: '100%',
        objectFit: 'contain',
        '--videoHeight': reactiveVideoRef?.videoHeight || 720,
        '--videoWidth': reactiveVideoRef?.videoWidth || 1080,
      }}
    />
  )
}

const MimicVideoTrack = ({
  track,
  fit,
}: Pick<VideoTrackProps, 'track' | 'fit'>) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    return () => {
      removeMimic(track.mediaStream, canvasRef.current)
    }
  }, [track.mediaStream])

  return (
    <canvas
      ref={(ref) => {
        if (ref) {
          attachMimic(track.mediaStream, ref)
          canvasRef.current = ref
        } else {
          removeMimic(track.mediaStream, canvasRef.current)
        }
      }}
      data-fit={fit}
      width="100%"
      height="100%"
      style={{
        display: 'block',
        height: '100%',
        width: '100%',
      }}
    />
  )
}

export { VideoTrack }
